// Deterministisches Terrain (Konzept §12.2). Gleiche Koordinate -> gleiche
// Höhe, immer, auf Server und Client. Kein Speichern, keine Synchronisation.
//
// Aufbau in drei Schichten:
//   1. Das handgeautorte Höhenprofil der Strecke (track.js) gibt die Grundhöhe.
//   2. Seitlich davon wächst das Gelände nach einem biomabhängigen Profil.
//   3. Korridor-Features (Einschnitt, Damm, Brücke) formen die Nahzone um.
//   4. Bahnhofshöfe: planierte Ebene über die ganze Hofbreite (s. unten).
//
// Der Trick, der prozedurale Landschaft glaubwürdig macht: Das Gleis liegt nie
// "auf" dem Rauschen, sondern das Rauschen weicht dem Gleis aus. Genau
// deshalb sieht es aus, als hätte jemand die Strecke gebaut.

import {
  projectToTrack, featureAt, biomeAt,
  STATIONS, DEAD_END, BRANCH_MINE,
} from './track.js';
import { fbm2, noise2, rand01 } from './rng.js';
import { WORLD_SEED } from './constants.js';

// Wie weit seitlich das Gleis das Gelände noch dominiert.
const BED_HALF = 3.6;      // Schotterbett
const SHOULDER = 9.0;      // Bankett/Böschung
const BLEND_OUT = 130;     // ab hier ist das Gelände frei

// --- Hof-Planum ------------------------------------------------------------
// WARUM (Variante 2, nicht „Gleis folgt Gelände“):
// In echten Rangierhöfen liegen Haupt- und Nebengleise auf *einer* planierten
// Ebene — man schüttet den Hof auf. Die Böschung (SHOULDER) beginnt sonst schon
// 3,6 m neben der Gleismitte und lässt Nebengleise (~9–15 m lateral) um ~2 m
// schweben. Ein Fix nur am Mesh würde Bahnsteige, Mauern, Kohlebunker, Arbeiter
// und den Spieler später auf derselben Böschung stehen lassen.
// Die Ebene folgt der Hauptgleishöhe entlang s (leichte Längsneigung bleibt
// fahrbar) und ist seitlich flach bis über das äußerste Nebengleis.
const YARD_BLEND_S = 55;    // weicher Ein-/Auslauf entlang der Strecke (m)
const YARD_BLEND_LAT = 22;  // weicher seitlicher Übergang ins freie Gelände (m)
const YARD_PAD = 5.0;       // Bankett jenseits des äußersten Gleises / Mauerfuß
// Schotteroberkante der Gleismitte — gleiche Höhe wie im Bett-Innen (s.u.).
const YARD_PLANE_OFFSET = 0.05;

const BIOME_PROFILE = {
  playa:  { relief: 2.2,  scale: 0.0055, ridge: 0.0,  roughness: 0.35 },
  scrub:  { relief: 7.5,  scale: 0.0075, ridge: 0.15, roughness: 0.5 },
  rock:   { relief: 26.0, scale: 0.0095, ridge: 0.6,  roughness: 0.8 },
  pine:   { relief: 18.0, scale: 0.0085, ridge: 0.35, roughness: 0.6 },
  meadow: { relief: 5.0,  scale: 0.006,  ridge: 0.05, roughness: 0.4 },
};

/** Freie Geländehöhe ohne Rücksicht auf das Gleis. */
function openTerrainHeight(x, z, baseY, biomeId) {
  const p = BIOME_PROFILE[biomeId] ?? BIOME_PROFILE.scrub;
  const nx = x * p.scale, nz = z * p.scale;

  let h = fbm2(nx, nz, WORLD_SEED, 4) * p.relief;

  // Ridged Noise für Felsgrate: |noise| invertiert erzeugt Kanten statt Hügel.
  if (p.ridge > 0) {
    const r = 1 - Math.abs(fbm2(nx * 1.7 + 31.7, nz * 1.7 - 12.3, WORLD_SEED ^ 0x9e3, 3));
    h += r * r * p.relief * p.ridge * 1.8;
  }

  // Feine Rauheit, damit die Nahzone nicht wie Butter aussieht.
  h += noise2(x * 0.09, z * 0.09, WORLD_SEED ^ 0x77) * p.roughness;

  return baseY + h;
}

/**
 * Halbe Breite der planierten Hofebene (Mitte → äußerer Rand).
 * Reicht über Nebengleise / Bahnsteig / Verladung plus Bankett.
 */
export function yardHalfWidth(st) {
  let maxLat = Math.abs(st.platformLateral || 0);
  const sidings = st.sidings;
  if (sidings) {
    for (let i = 0; i < sidings.length; i++) {
      const a = Math.abs(sidings[i].lateral);
      if (a > maxLat) maxLat = a;
    }
  }
  // Copper Spur: Verladung und Halde sitzen ~7,5–12 m lateral (track_mesh).
  if (st.deadEnd) maxLat = Math.max(maxLat, 12);
  return maxLat + YARD_PAD;
}

/** smoothstep 0..1 — C¹ an den Rändern, keine Klippenkante im Gewicht. */
function smooth01(t) {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

/**
 * Gewicht 0..1 der Hofebene an (s, lateral) auf der gegebenen Strecke.
 * Deterministisch, O(1) Höfe, keine Allokation — läuft pro Terrain-Vertex.
 */
export function yardPlaneWeight(s, lateral, branch) {
  const absLat = lateral < 0 ? -lateral : lateral;
  let w = 0;
  if (branch === BRANCH_MINE) {
    w = yardWeightOne(s, absLat, DEAD_END);
  } else {
    // Hauptstrecke: beide Höfe (kein DEAD_END — der liegt auf der Mine).
    for (let i = 0; i < STATIONS.length; i++) {
      const wi = yardWeightOne(s, absLat, STATIONS[i]);
      if (wi > w) w = wi;
    }
  }
  return w;
}

function yardWeightOne(s, absLat, st) {
  const y0 = st.yardStart;
  const y1 = st.yardEnd;
  let wS;
  if (s >= y0 && s <= y1) {
    wS = 1;
  } else if (s > y0 - YARD_BLEND_S && s < y0) {
    wS = smooth01((s - (y0 - YARD_BLEND_S)) / YARD_BLEND_S);
  } else if (s > y1 && s < y1 + YARD_BLEND_S) {
    wS = smooth01(((y1 + YARD_BLEND_S) - s) / YARD_BLEND_S);
  } else {
    return 0;
  }

  const half = yardHalfWidth(st);
  let wL;
  if (absLat <= half) {
    wL = 1;
  } else if (absLat < half + YARD_BLEND_LAT) {
    wL = 1 - smooth01((absLat - half) / YARD_BLEND_LAT);
  } else {
    return 0;
  }
  return wS * wL;
}

/**
 * Höhe des Geländes an einer Weltposition.
 * hint: letzter bekannter s-Wert, beschleunigt die Projektion massiv.
 */
export function terrainHeight(x, z, hint = -1) {
  const proj = projectToTrack(x, z, hint);
  return terrainHeightAt(x, z, proj);
}

/** Variante, wenn die Projektion schon vorliegt (spart Arbeit). */
export function terrainHeightAt(x, z, proj) {
  const { s, lateral, trackY } = proj;
  const branch = proj.branch;
  const absLat = Math.abs(lateral);
  // Branch mitgeben: Mine-Biome/Features sonst fälschlich von der Hauptstrecke.
  const biome = biomeAt(s, branch);
  const feature = featureAt(s, branch);

  const open = openTerrainHeight(x, z, trackY, biome.id);

  // --- Nahzone: das Gleis formt das Gelände ---------------------------------
  let h;
  // Schotterbett: flach, ganz leicht gewölbt.
  if (absLat <= BED_HALF) {
    h = trackY - YARD_PLANE_OFFSET - (absLat / BED_HALF) * 0.55;
  } else if (feature?.type === 'cutting') {
    // Einschnitt: Wände steigen steil an.
    const depth = feature.depth ?? 6;
    const t = clamp01((absLat - BED_HALF) / 14);
    const nearY = trackY - 0.6 + Math.pow(t, 0.7) * (depth + 4);
    // Oberkante geht in freies Gelände über, aber nie unter die Wandhöhe.
    const blend = clamp01((absLat - 16) / 30);
    h = Math.max(nearY, lerp(nearY, open, blend));
  } else if (feature?.type === 'embankment' || feature?.type === 'bridge') {
    const drop = feature.type === 'bridge' ? (feature.height ?? 20) : (feature.height ?? 6);
    const t = clamp01((absLat - BED_HALF) / (feature.type === 'bridge' ? 22 : 16));
    const nearY = trackY - 0.6 - Math.pow(t, 0.8) * drop;
    const blend = clamp01((absLat - (feature.type === 'bridge' ? 26 : 20)) / 40);
    h = lerp(nearY, Math.min(open, nearY + 3), blend);
  } else if (feature?.type === 'wash') {
    // Trockenfluss quer zur Strecke: Betonwanne, seitlich abfallend.
    const t = clamp01((absLat - BED_HALF) / 12);
    const nearY = trackY - 0.6 - t * 3.2;
    const blend = clamp01((absLat - 14) / 30);
    h = lerp(nearY, open, blend);
  } else {
    // Regelfall: leichter Damm, dann freies Gelände.
    const shoulderT = clamp01((absLat - BED_HALF) / (SHOULDER - BED_HALF));
    const nearY = trackY - 0.6 - shoulderT * 1.4;
    const blend = clamp01((absLat - SHOULDER) / (BLEND_OUT - SHOULDER));
    const eased = blend * blend * (3 - 2 * blend);
    h = lerp(nearY, open, eased);
  }

  // --- Hof-Planum: Gelände folgt dem Gleis, nicht umgekehrt -----------------
  // Gewicht 0 außerhalb; weich an s- und Lateral-Rändern (keine Klippe).
  const yw = yardPlaneWeight(s, lateral, branch);
  if (yw > 0) {
    const planeY = trackY - YARD_PLANE_OFFSET;
    h = lerp(h, planeY, yw);
  }
  return h;
}

/** Geländenormale (für Ausrichtung von Objekten und Beleuchtung). */
export function terrainNormal(x, z, hint = -1) {
  const e = 1.2;
  const hL = terrainHeight(x - e, z, hint);
  const hR = terrainHeight(x + e, z, hint);
  const hD = terrainHeight(x, z - e, hint);
  const hU = terrainHeight(x, z + e, hint);
  const nx = hL - hR;
  const nz = hD - hU;
  const ny = 2 * e;
  const len = Math.hypot(nx, ny, nz) || 1;
  return { x: nx / len, y: ny / len, z: nz / len };
}

/**
 * Ist an dieser Position eine begehbare Fläche, oder Luft (Brücke)?
 * Auf Brücken gibt es neben dem Gleis nichts — ein Fehltritt ist final.
 */
export function isVoidAt(s, lateral) {
  const f = featureAt(s);
  if (f?.type === 'bridge' && Math.abs(lateral) > 3.2) return true;
  return false;
}

/**
 * Sichtlinie zwischen zwei Punkten, grob gesampelt.
 * Wird für die geländeabhängige Funkreichweite benutzt (Konzept §9).
 * Liefert 0..1: 1 = freie Sicht, 0 = vollständig verdeckt.
 */
export function lineOfSight(ax, ay, az, bx, by, bz, samples = 24) {
  const dist = Math.hypot(bx - ax, bz - az);
  if (dist < 1) return 1;
  let worst = 1;
  for (let i = 1; i < samples; i++) {
    const t = i / samples;
    const x = ax + (bx - ax) * t;
    const z = az + (bz - az) * t;
    const rayY = ay + (by - ay) * t;
    const ground = terrainHeight(x, z);
    const clearance = rayY - ground;
    if (clearance < 0) {
      // Wie tief steckt der Strahl im Berg? Weiche Dämpfung statt harter Kante.
      const blocked = Math.max(0, 1 + clearance / 18);
      worst = Math.min(worst, blocked);
    }
  }
  return clamp01(worst);
}

/** Streudetail (Bäume, Büsche, Schrott) an einer Zelle — deterministisch. */
export function scatterAt(cellX, cellZ, biomeId) {
  const r = rand01(cellX, cellZ, WORLD_SEED ^ 0x5ca7);
  const density = {
    playa: 0.06, scrub: 0.28, rock: 0.16, pine: 0.62, meadow: 0.22,
  }[biomeId] ?? 0.2;
  if (r > density) return null;
  const kind = pickScatterKind(biomeId, cellX, cellZ);
  return {
    kind,
    jitterX: (rand01(cellX, cellZ, 11) - 0.5) * 0.85,
    jitterZ: (rand01(cellX, cellZ, 22) - 0.5) * 0.85,
    scale: 0.7 + rand01(cellX, cellZ, 33) * 0.75,
    rotation: rand01(cellX, cellZ, 44) * Math.PI * 2,
  };
}

function pickScatterKind(biomeId, cx, cz) {
  const r = rand01(cx, cz, 0xbeef);
  switch (biomeId) {
    case 'playa':  return r < 0.7 ? 'bush-dry' : 'rock-small';
    case 'scrub':  return r < 0.5 ? 'bush-dry' : r < 0.8 ? 'joshua' : 'rock-small';
    case 'rock':   return r < 0.6 ? 'rock-large' : r < 0.85 ? 'rock-small' : 'pine-dead';
    case 'pine':   return r < 0.75 ? 'pine' : r < 0.9 ? 'pine-dead' : 'bush-green';
    case 'meadow': return r < 0.55 ? 'bush-green' : r < 0.8 ? 'pine' : 'rock-small';
    default:       return 'bush-dry';
  }
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
function lerp(a, b, t) { return a + (b - a) * t; }

export { clamp01, lerp, YARD_BLEND_S, YARD_BLEND_LAT };
